import { RichText, RichTextValue } from "@/components/rich-text"
import { SlideOnView } from "@/components/slide-on-view"
import { GridBg } from "@/svgs/grid-bg"
import { cn } from "@/utils/cn"
import {
  BadgeCheck,
  BanknoteX,
  BrainCircuit,
  CircleHelp,
  Cpu,
  Gauge,
  HandCoins,
  MessageSquare,
  MessagesSquare,
  Rocket,
  ShieldCheck,
  TrendingUp,
  UserCheck,
  UsersRound,
} from "lucide-react"
import { ComponentType } from "react"

export type TwoColumnListIconName =
  | "rocket"
  | "trending-up"
  | "brain-circuit"
  | "cpu"
  | "user-check"
  | "shield-check"
  | "message-square"
  | "hand-coins"
  | "gauge"
  | "users-round"
  | "messages-square"
  | "banknote-x"

type TwoColumnListItem = {
  label: string
  icon: TwoColumnListIconName | string
}

const iconMap: Record<
  TwoColumnListIconName,
  ComponentType<{ className?: string }>
> = {
  rocket: Rocket,
  "trending-up": TrendingUp,
  "brain-circuit": BrainCircuit,
  cpu: Cpu,
  "user-check": UserCheck,
  "shield-check": ShieldCheck,
  "message-square": MessageSquare,
  "hand-coins": HandCoins,
  gauge: Gauge,
  "users-round": UsersRound,
  "messages-square": MessagesSquare,
  "banknote-x": BanknoteX,
}

interface TwoColumnRichTextProps {
  content: {
    bgColor: "white" | "blue"
    title: RichTextValue
    body: RichTextValue
    titleList?: TwoColumnListItem[]
    bodyList?: TwoColumnListItem[]
  }
}

export function TwoColumnRichText({ content }: TwoColumnRichTextProps) {
  const isBlue = content.bgColor === "blue"
  const iconColorClass = isBlue ? "text-green-500" : "text-primary-500"

  function renderList(items?: TwoColumnListItem[]) {
    if (!items?.length) return null

    return (
      <ul className="mt-8 space-y-4">
        {items.map((item) => {
          const Icon = iconMap[item.icon as TwoColumnListIconName] ?? CircleHelp

          return (
            <li
              key={`${item.icon}-${item.label}`}
              className="flex items-start gap-3"
            >
              <Icon className={cn("mt-1 h-5 w-5 shrink-0", iconColorClass)} />
              <span className="font-light">{item.label}</span>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <section
      className={cn(
        "relative overflow-hidden",
        isBlue
          ? "bg-primary-800 text-neutral-light"
          : "bg-neutral-light text-neutral-dark"
      )}
    >
      {/* Animated grid background */}
      <GridBg
        className={cn(
          "absolute inset-0 -z-0",
          isBlue ? "text-viola-500/30" : "text-viola-500/40"
        )}
      />

      {/* Mobile: single centered glow */}
      <div
        className={cn(
          "pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:hidden",
          "h-[50%] w-[60%] rounded-full blur-[80px]",
          isBlue ? "bg-viola-500/20" : "bg-viola-300/35"
        )}
      />
      {/* Desktop: left glow */}
      <div
        className={cn(
          "pointer-events-none absolute top-1/2 -left-[10%] hidden -translate-y-1/2 md:block",
          "h-[60%] w-[40%] rounded-full blur-[80px]",
          isBlue ? "bg-viola-500/20" : "bg-viola-300/35"
        )}
      />
      {/* Desktop: right glow */}
      <div
        className={cn(
          "pointer-events-none absolute top-1/2 -right-[10%] hidden -translate-y-1/2 md:block",
          "h-[60%] w-[40%] rounded-full blur-[80px]",
          isBlue ? "bg-viola-500/20" : "bg-viola-300/35"
        )}
      />

      <div className="relative z-10 mx-auto max-w-[166rem] px-6 py-[12vh] md:px-16 lg:px-20 lg:py-[30vh] xl:px-35">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-20 xl:gap-28">
          <SlideOnView
            className={cn(
              "font-family-gthaptik text-lg leading-[1.35] xl:text-xlg",
              "[&_p]:mb-6 [&_p]:last:mb-0"
            )}
          >
            <RichText value={content.title} />
            {renderList(content.titleList)}
          </SlideOnView>
          <SlideOnView
            delay={0.2}
            className={cn(
              "font-family-gthaptik text-lg leading-[1.35] xl:text-xlg",
              "[&_p]:mb-6 [&_p]:last:mb-0"
            )}
          >
            <RichText value={content.body} />
            {renderList(content.bodyList)}
          </SlideOnView>
        </div>
      </div>
    </section>
  )
}
